import { ticketService } from '@/services/ticket-service';
import './nu-user-menu';
import { applyDevWatermark } from '@/services/dev-watermark';

type SubscribeState = 'loading' | 'input' | 'submitting' | 'success' | 'error_not_found' | 'error_phone' | 'error_generic' | 'error_spent';

class NuSubscribe extends HTMLElement {
  private readonly root: ShadowRoot;
  private state: SubscribeState = 'loading';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['token']; }

  get token() { return this.getAttribute('token') ?? ''; }

  connectedCallback() {
    this._render();
    applyDevWatermark(this.root);
    this._checkTokenStatus();
  }

  private async _checkTokenStatus() {
    try {
      const status = await ticketService.getSubscriptionTokenStatus(this.token);
      this.state = status.canSubscribe ? 'input' : 'error_spent';
    } catch (err) {
      if (err instanceof Error && err.message === 'token_not_found') {
        this.state = 'error_not_found';
      } else {
        this.state = 'error_generic';
      }
    }
    this._render();
  }

  private _render() {
    this.root.innerHTML = this._styles() + this._body();
    this._bindEvents();
  }

  private _body(): string {
    return `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>notify-u</span>
          <nu-user-menu></nu-user-menu>
        </header>
        <div class="card-wrap">
          <div class="card">
            <div class="perf"></div>
            <div class="card-body">
              ${this._content()}
            </div>
            <div class="perf"></div>
          </div>
        </div>
      </div>
    `;
  }

  private _content(): string {
    switch (this.state) {
      case 'loading':
        return `<div class="spinner-lg" aria-label="Loading…"></div>`;

      case 'input':
      case 'submitting':
      case 'error_phone':
      case 'error_generic':
        return `
          <div class="qr-icon" aria-hidden="true">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="2" width="13" height="13" rx="2" fill="currentColor"/>
              <rect x="4.5" y="4.5" width="8" height="8" rx="1" fill="var(--paper-light,#FAF7F0)"/>
              <rect x="6" y="6" width="5" height="5" fill="currentColor"/>
              <rect x="21" y="2" width="13" height="13" rx="2" fill="currentColor"/>
              <rect x="23.5" y="4.5" width="8" height="8" rx="1" fill="var(--paper-light,#FAF7F0)"/>
              <rect x="25" y="6" width="5" height="5" fill="currentColor"/>
              <rect x="2" y="21" width="13" height="13" rx="2" fill="currentColor"/>
              <rect x="4.5" y="23.5" width="8" height="8" rx="1" fill="var(--paper-light,#FAF7F0)"/>
              <rect x="6" y="25" width="5" height="5" fill="currentColor"/>
              <rect x="21" y="21" width="5" height="5" fill="currentColor"/>
              <rect x="30" y="21" width="5" height="5" fill="currentColor"/>
              <rect x="21" y="30" width="5" height="5" fill="currentColor"/>
              <rect x="30" y="30" width="5" height="5" fill="currentColor"/>
            </svg>
          </div>
          <h1 class="heading">Get notified when it's ready</h1>
          <p class="subtext">Enter your phone number and we'll send you a text when your item is ready for pickup.</p>
          ${this.state === 'error_phone' ? `<p class="field-error">Please enter a valid 10-digit phone number.</p>` : ''}
          ${this.state === 'error_generic' ? `<p class="field-error">Something went wrong. Please try again.</p>` : ''}
          <label class="eyebrow mono" for="phone-input">Phone number</label>
          <input class="field-input" id="phone-input" type="tel"
            placeholder="555-867-5309" autocomplete="tel" inputmode="tel"
            ${this.state === 'submitting' ? 'disabled' : ''}>
          <button class="btn-primary" id="subscribe-btn" ${this.state === 'submitting' ? 'disabled' : ''}>
            ${this.state === 'submitting' ? '<span class="spinner-sm"></span> Subscribing…' : 'Notify me'}
          </button>
        `;

      case 'success':
        return `
          <div class="success-icon" aria-hidden="true">✓</div>
          <h1 class="heading">You're subscribed!</h1>
          <p class="subtext">We'll send you a text message when your item is ready for pickup. No app needed.</p>
        `;

      case 'error_not_found':
        return `
          <div class="error-icon" aria-hidden="true">✕</div>
          <h1 class="heading">Link not found</h1>
          <p class="subtext">This QR code doesn't appear to be active. Ask a staff member for help.</p>
        `;

      case 'error_spent':
        return `
          <div class="error-icon" aria-hidden="true">✕</div>
          <h1 class="heading">Notifications already sent</h1>
          <p class="subtext">This QR code has already been used and pickup notifications have been delivered. Ask a staff member if you need help.</p>
        `;
    }
  }

  private _bindEvents() {
    const btn = this.root.querySelector<HTMLButtonElement>('#subscribe-btn');
    if (!btn) return;

    btn.addEventListener('click', () => this._submit());

    this.root.querySelector<HTMLInputElement>('#phone-input')
      ?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this._submit();
      });
  }

  private async _submit() {
    const input = this.root.querySelector<HTMLInputElement>('#phone-input');
    const phoneNum = input?.value.trim() ?? '';

    const digits = phoneNum.replace(/\D/g, '');
    if (digits.length !== 10) {
      this.state = 'error_phone';
      this._render();
      this.root.querySelector<HTMLInputElement>('#phone-input')!.value = phoneNum;
      return;
    }

    this.state = 'submitting';
    this._render();

    try {
      await ticketService.subscribe(this.token, phoneNum);
      this.state = 'success';
    } catch (err) {
      if (err instanceof Error && err.message === 'token_not_found') {
        this.state = 'error_not_found';
      } else if (err instanceof Error && err.message === 'token_spent') {
        this.state = 'error_spent';
      } else {
        this.state = 'error_generic';
      }
    }
    this._render();
  }

  private _styles(): string {
    return `<style>
      :host { display: block; }
      *, *::before, *::after { box-sizing: border-box; }

      .page {
        min-height: 100vh;
        background: var(--paper, #E4DED2);
        color: var(--ink, #2A2622);
        font-family: 'Inter', sans-serif;
        line-height: 1.55;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .mono { font-family: 'IBM Plex Mono', monospace; }

      .top-bar {
        width: 100%;
        display: flex;
        align-items: center;
        padding: 14px 20px;
        border-bottom: 1px solid var(--line, #C9C0AC);
        background: rgba(228,222,210,0.92);
        backdrop-filter: blur(6px);
        position: sticky;
        top: 0;
        z-index: 10;
      }
      .brand {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700;
        font-size: 18px;
        letter-spacing: -0.02em;
        display: flex;
        align-items: center;
        gap: 7px;
      }
      .dot {
        width: 8px; height: 8px;
        border-radius: 50%;
        background: var(--kiln, #C97A2E);
        display: inline-block;
        flex-shrink: 0;
      }

      .card-wrap {
        width: 100%;
        max-width: 420px;
        padding: 32px 20px 20px;
      }
      .card {
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        overflow: hidden;
      }
      .perf {
        height: 12px;
        background-image: radial-gradient(circle at 6px 6px, var(--paper, #E4DED2) 4px, var(--paper-light, #FAF7F0) 4.3px);
        background-size: 16px 12px;
        background-repeat: repeat-x;
      }
      .card-body {
        padding: 28px 24px 32px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 0;
      }

      .qr-icon {
        color: var(--ink, #2A2622);
        margin-bottom: 18px;
      }
      .success-icon {
        width: 56px; height: 56px;
        border-radius: 50%;
        background: var(--glaze, #6E7F5C);
        color: #fff;
        font-size: 26px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 18px;
      }
      .error-icon {
        width: 56px; height: 56px;
        border-radius: 50%;
        background: #C97A2E;
        color: #fff;
        font-size: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 18px;
      }

      .heading {
        font-family: 'Space Grotesk', sans-serif;
        font-size: 20px;
        font-weight: 700;
        margin: 0 0 10px;
        letter-spacing: -0.02em;
      }
      .subtext {
        font-size: 14px;
        color: var(--ink-soft, #5A5346);
        max-width: 30ch;
        margin: 0 0 24px;
      }
      .field-error {
        width: 100%;
        font-size: 12px;
        color: #b94a2c;
        margin: 0 0 8px;
        text-align: left;
        font-family: 'IBM Plex Mono', monospace;
      }

      .eyebrow {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 11px;
        letter-spacing: .08em;
        text-transform: uppercase;
        color: var(--kiln-dark, #A15F1F);
        margin: 0 0 6px;
        display: block;
        align-self: flex-start;
      }
      .field-input {
        display: block;
        width: 100%;
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px;
        padding: 12px 14px;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 16px;
        color: var(--ink, #2A2622);
        margin-bottom: 14px;
        text-align: left;
      }
      .field-input:focus {
        outline: 2px solid var(--kiln, #C97A2E);
        outline-offset: 1px;
        border-color: transparent;
      }
      .field-input:disabled { opacity: 0.55; }

      .btn-primary {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        background: var(--kiln, #C97A2E);
        color: var(--paper-light, #FAF7F0);
        border: none;
        border-radius: 5px;
        padding: 14px;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 15px;
        cursor: pointer;
        transition: background .15s, transform .1s;
      }
      .btn-primary:hover:not(:disabled) { background: var(--kiln-dark, #A15F1F); }
      .btn-primary:active:not(:disabled) { transform: translateY(1px); }
      .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

      .spinner-sm {
        width: 16px; height: 16px;
        border: 2px solid rgba(250,247,240,.4);
        border-top-color: var(--paper-light, #FAF7F0);
        border-radius: 50%;
        animation: spin .7s linear infinite;
        flex-shrink: 0;
      }
      .spinner-lg {
        width: 36px; height: 36px;
        border: 3px solid var(--line, #C9C0AC);
        border-top-color: var(--kiln, #C97A2E);
        border-radius: 50%;
        animation: spin .7s linear infinite;
        margin: 8px auto 8px;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
    </style>`;
  }
}

customElements.define('nu-subscribe', NuSubscribe);